import { Component, Input, OnInit, OnDestroy } from '@angular/core';

import { Subject } from 'rxjs';

import { takeUntil } from 'rxjs/operators';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { ProjectTaskService } from 'src/app/private/services/project-task.service';

import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-project-task-activity',
  templateUrl: './project-task-activity.component.html',
  styleUrls: ['./project-task-activity.component.scss'],
})
export class ProjectTaskActivityComponent implements OnInit, OnDestroy {
  @Input() taskId!: any;

  @Input() projectId!: any;

  private destroy$ = new Subject<void>();

  items: any[] = [];

  loading = true;

  page = 1;

  size = 50;

  total = 0;

  // ── Activity Type Config ──────────────────────────────────────────────────

  readonly ACTIVITY_CONFIG: Record<
    string,
    {
      icon: string;
      label: string;
      colorClass: string;
      source: string;
    }
  > = {
    // ── Task ────────────────────────────────────────────────────────────────
    create: {
      icon: 'fa-plus-circle',
      label: 'Task Created',
      colorClass: 'hl-create',
      source: 'Task',
    },

    update: {
      icon: 'fa-pencil',
      label: 'Task Updated',
      colorClass: 'hl-update',
      source: 'Task',
    },

    delete: {
      icon: 'fa-trash',
      label: 'Task Deleted',
      colorClass: 'hl-delete',
      source: 'Task',
    },

    status_change: {
      icon: 'fa-refresh',
      label: 'Status Changed',
      colorClass: 'hl-status',
      source: 'Task',
    },

    priority_change: {
      icon: 'fa-exclamation-triangle',
      label: 'Priority Changed',
      colorClass: 'hl-priority',
      source: 'Task',
    },

    assignment: {
      icon: 'fa-user-plus',
      label: 'Assigned',
      colorClass: 'hl-assigned',
      source: 'Task',
    },

    due_date_change: {
      icon: 'fa-calendar',
      label: 'Due Date Changed',
      colorClass: 'hl-due',
      source: 'Task',
    },

    attachment_add: {
      icon: 'fa-paperclip',
      label: 'Attachment Added',
      colorClass: 'hl-attachment',
      source: 'Task',
    },

    attachment_remove: {
      icon: 'fa-paperclip',
      label: 'Attachment Removed',
      colorClass: 'hl-delete',
      source: 'Task',
    },

    // ── Subtask ─────────────────────────────────────────────────────────────
    subtask_create: {
      icon: 'fa-plus-square',
      label: 'Subtask Created',
      colorClass: 'hl-create',
      source: 'Subtask',
    },

    subtask_update: {
      icon: 'fa-pencil-square-o',
      label: 'Subtask Updated',
      colorClass: 'hl-update',
      source: 'Subtask',
    },

    subtask_delete: {
      icon: 'fa-minus-square',
      label: 'Subtask Deleted',
      colorClass: 'hl-delete',
      source: 'Subtask',
    },

    subtask_status_change: {
      icon: 'fa-check-square',
      label: 'Subtask Status Changed',
      colorClass: 'hl-status',
      source: 'Subtask',
    },

    // ── Comments ────────────────────────────────────────────────────────────
    comment: {
      icon: 'fa-comment',
      label: 'Comment Added',
      colorClass: 'hl-comment',
      source: 'Comment',
    },

    comment_create: {
      icon: 'fa-comment',
      label: 'Comment Added',
      colorClass: 'hl-comment',
      source: 'Comment',
    },

    comment_delete: {
      icon: 'fa-comment-o',
      label: 'Comment Deleted',
      colorClass: 'hl-delete',
      source: 'Comment',
    },

    // ── Time Logs ───────────────────────────────────────────────────────────
    time_log_create: {
      icon: 'fa-clock-o',
      label: 'Time Logged',
      colorClass: 'hl-timelog',
      source: 'Time Log',
    },

    time_log_update: {
      icon: 'fa-clock-o',
      label: 'Time Log Updated',
      colorClass: 'hl-update',
      source: 'Time Log',
    },

    time_log_delete: {
      icon: 'fa-clock-o',
      label: 'Time Log Deleted',
      colorClass: 'hl-delete',
      source: 'Time Log',
    },
  };

  // ── Field Labels ──────────────────────────────────────────────────────────

  readonly FIELD_LABELS: Record<string, string> = {
    title: 'Title',
    description: 'Description',
    status: 'Status',
    priority: 'Priority',
    assignee_id: 'Assignee',
    assignee: 'Assignee',
    reviewer_id: 'Reviewer',
    due_date: 'Due Date',
    start_date: 'Start Date',
    estimated_minutes: 'Estimated Minutes',
    actual_minutes: 'Actual Minutes',
    progress_percentage: 'Progress',
    item_type: 'Item Type',
    is_billable: 'Billable',
    total_minutes: 'Total Minutes',
    work_date: 'Work Date',
    sort_order: 'Sort Order',
    is_completed: 'Completed',
    blocked_by_item_id: 'Blocked By',
    parent_item_id: 'Parent Item',
    task_type: 'Task Type',
    module_id: 'Module',
    department_id: 'Department',
    bug_id: 'Bug',
    comment: 'Comment Text',
    remarks: 'Remarks',
    attachments: 'Attachments',
    project: 'Project',
  };

  // ── Ignored Fields ────────────────────────────────────────────────────────

  readonly IGNORED_CHANGE_FIELDS = new Set([
    '_isUpdate',
    'project_task_items',
    'updated_at',
    'created_at',
  ]);

  constructor(
    private api: ProjectTaskService,
    private localStorage: LocalStorageService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    if (this.taskId) {
      this.loadActivities();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();

    this.destroy$.complete();
  }

  // ── Load Activities ───────────────────────────────────────────────────────

  loadActivities(page = 1) {
    this.loading = true;

    this.page = page;

    this.api
      .getTaskActivities({
        task_id: this.taskId,

        project_id: this.projectId,

        client_id: this.localStorage.getClientId(),

        page: this.page,

        limit: this.size,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.items = data?.rows ?? data?.items ?? data ?? [];

          this.total = data?.total ?? data?.count ?? this.items.length;

          this.loading = false;
        },

        error: () => {
          this.loading = false;
        },
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  getConfig(activityType: string) {
    return (
      this.ACTIVITY_CONFIG[activityType] ?? {
        icon: 'fa-info-circle',

        label: this.prettyType(activityType),

        colorClass: 'hl-default',

        source: 'System',
      }
    );
  }

  prettyType(type: string): string {
    if (!type) return '';

    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  getFieldLabel(field: string): string {
    return this.FIELD_LABELS[field] || this.prettyType(field);
  }

  formatValue(val: any): string {
    if (val === null || val === undefined || val === '') {
      return '—';
    }

    if (val === true || val === 'true') {
      return 'Yes';
    }

    if (val === false || val === 'false') {
      return 'No';
    }

    // JSON Parse
    if (
      typeof val === 'string' &&
      (val.startsWith('{') || val.startsWith('['))
    ) {
      try {
        val = JSON.parse(val);
      } catch {}
    }

    if (typeof val === 'object') {
      // Person object
      if (val.fname !== undefined || val.lname !== undefined) {
        return [val.fname, val.lname].filter(Boolean).join(' ').trim() || '—';
      }

      // Named object
      if (val.name !== undefined) {
        return String(val.name);
      }

      try {
        return JSON.stringify(val, null, 0);
      } catch {
        return String(val);
      }
    }

    // Date
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(val)) {
      const d = new Date(val);

      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString([], {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      }
    }

    return String(val);
  }

  formatTime(ts: string): string {
    if (!ts) return '';

    const d = new Date(ts);

    return d.toLocaleString([], {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  relativeTime(ts: string): string {
    if (!ts) return '';

    const diff = Date.now() - new Date(ts).getTime();

    const mins = Math.floor(diff / 60000);

    if (mins < 1) {
      return 'just now';
    }

    if (mins < 60) {
      return `${mins}m ago`;
    }

    const hrs = Math.floor(mins / 60);

    if (hrs < 24) {
      return `${hrs}h ago`;
    }

    const days = Math.floor(hrs / 24);

    if (days < 7) {
      return `${days}d ago`;
    }

    return this.formatTime(ts);
  }

  safeHtml(msg: any): SafeHtml {
    if (!msg || msg === 'null' || msg === 'undefined') {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    return this.sanitizer.bypassSecurityTrustHtml(
      String(msg).replace(/undefined|null/gi, '—'),
    );
  }

  hasLegacyFieldChange(ev: any): boolean {
    return !!(
      ev.field_name ||
      ev.old_value !== undefined ||
      ev.new_value !== undefined
    );
  }

  // ── Process Changes ───────────────────────────────────────────────────────

  getMeaningfulChanges(ev: any): Array<{
    field: string;
    fieldLabel: string;
    oldDisplayVal: string | null;
    newDisplayVal: string;
  }> {
    const changes: any[] = ev.changes;

    if (!Array.isArray(changes) || changes.length === 0) {
      return [];
    }

    return changes
      .filter((ch) => {
        if (this.IGNORED_CHANGE_FIELDS.has(ch.field)) {
          return false;
        }

        const hasNew =
          ch.newVal !== null && ch.newVal !== undefined && ch.newVal !== '';

        const hasOld =
          ch.oldVal !== null && ch.oldVal !== undefined && ch.oldVal !== '';

        return hasNew || hasOld;
      })

      .map((ch) => ({
        field: ch.field,

        fieldLabel: ch.fieldLabel || this.getFieldLabel(ch.field),

        oldDisplayVal:
          ch.oldVal !== null && ch.oldVal !== undefined && ch.oldVal !== ''
            ? this.formatValue(ch.oldVal)
            : null,

        newDisplayVal: this.formatValue(ch.newVal),
      }));
  }

  // ── Initials ──────────────────────────────────────────────────────────────

  getInitials(name: string): string {
    if (!name) return '?';

    const parts = name.trim().split(/\s+/);

    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }

    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  }

  // ── Pagination ────────────────────────────────────────────────────────────

  get totalPages(): number {
    return Math.ceil(this.total / this.size);
  }
}
